/**
 * Future Closure Parameter Preview Component
 *
 * Forensic-integrity note (2026-06-30): this component intentionally does NOT
 * compute a dollar demand. The deterministic backend engine
 * (POST /api/v1/future-closure/calculate/{report_id}, FutureClosureEngine) is
 * the single source of truth for every financial value — lost profits,
 * continuing expenses, one-time costs, and total demand. A client-side
 * multiplier-chain estimate (the prior "Estimated Total Demand") could diverge
 * from the engine and mislead an attorney, so it has been removed per
 * CLAUDE.md rule "LLMs Interpret, Code Calculates — code calculates all
 * financial values in deterministic Python engines."
 *
 * This panel now only echoes the selected, non-financial parameters so the
 * user can confirm what will be sent to the engine on "Run Calculation".
 */

import React from 'react';

interface LivePreviewProps {
  aggression: number;
  baseMonths: number;
  useBestBase: boolean;
  closureDuration: number;
  customContinuations?: {
    officer_salary: number;
    employee_wages: number;
    rent: number;
    utilities: number;
    insurance: number;
    other_expenses: number;
  };
  reportData?: any;
}

export default function FutureClosureLivePreview({
  aggression,
  baseMonths,
  useBestBase,
  closureDuration,
  reportData
}: LivePreviewProps) {
  if (!reportData?.financial_data) {
    return null;
  }

  return (
    <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      <div className="flex items-start space-x-2">
        <div>
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Selected Parameters</p>
          <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
            The demand is computed by the deterministic engine when you run the calculation — no estimate is shown here.
          </p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex justify-between text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
          <span>Aggression: {aggression}/100</span>
          <span>Duration: {closureDuration} mo</span>
          <span>Base: {useBestBase ? 'Best' : 'Last'} {baseMonths} mo</span>
        </div>
      </div>

      <p className="mt-2 text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
        Click "Run Calculation" to compute the demand from your financial data.
      </p>
    </div>
  );
}
